import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Observable, throwError } from "rxjs";
import { catchError, finalize, switchMap, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { CommonApiservice } from "../services/common.api.service";
import { EventService } from "../services/event.service";
import { LocalStorageProvider } from "../services/localStorage";
import { PopupService } from "../services/popup.service";
import { EVENT, KTO_STATUS_CODE, TOAST_MESSAGES } from "../utils/enum";
import { MESSAGES } from "../utils/messages";

const SERVER_ERROR_CODE = [
  KTO_STATUS_CODE.ERR_114,
  KTO_STATUS_CODE.ERR_115,
  KTO_STATUS_CODE.ERR_153,
  KTO_STATUS_CODE.ERR_401,
];

@Injectable({
  providedIn: "root",
})
export class AppInterceptor implements HttpInterceptor {
  isRefreshing: boolean = false;
  requests: HttpRequest<any>[] = [];
  requestCount = 0;

  constructor(
    private route: Router,
    public commonService: CommonApiservice,
    public localStorage: LocalStorageProvider,
    private toastService: PopupService,
    private dialogRef: MatDialog,
    private eventService: EventService,
  ) { }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const token = this.isRefreshing
      ? ""
      : this.localStorage.getItem("accesstoken");
    const cloneReq = this.addTokenHeader(request, token);
    this.beforeRequest(request);
    return next.handle(cloneReq).pipe(
      tap((event: HttpEvent<any>) => {
        this.afterRequest();
      }),
      catchError((error: HttpErrorResponse) => {
        this.afterRequest();
        if (SERVER_ERROR_CODE.includes(error.error?.serverCode)) {
          if (error.error?.serverCode === KTO_STATUS_CODE.ERR_153) {
            this.logout();
            this.toastService.popupMessage(
              TOAST_MESSAGES.DISABLE,
              MESSAGES.TRANSACTION_SUPPORT,
              MESSAGES.ACCOUNT_BLOCKED,
              "Close",
            );
            return throwError(() => error);
          } else if (request.url.includes("refreshToken")) {
            this.logout();
            return throwError(() => error);
          } else {
            return this.handleForbiddenError(request, next);
          }
        } else if (error.status === 504) {
          return throwError(() => {
            return { message: MESSAGES.REQUEST_TIME_OUT };
          });
        } else {
          if (request.url.indexOf("verifyKyc") == -1) {
            this.toastService.toastMessage(
              TOAST_MESSAGES.ERROR,
              error.error.message ??
              "Something went wrong, please contact platform admin.",
            );
          }
          return throwError(() => error);
        }
      }),
      finalize(() => {
        this.afterRequest();
      }),
    );
  }

  private handleForbiddenError(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const refreshToken = this.localStorage.getItem("refreshToken");

    if (refreshToken) {
      return this.commonService
        .refreshToken({ refresh_token: refreshToken })
        .pipe(
          switchMap((res: any) => {
            this.isRefreshing = false;
            if (res.success) {
              this.localStorage.setItem("USER_TOKEN", res.result.access_token);
              this.localStorage.setItem("accesstoken", res.result.accessToken);
              this.localStorage.setItem(
                "refreshToken",
                res.result.refreshToken,
              );
              return next.handle(
                this.addTokenHeader(request, res.result.accessToken),
              );
            } else {
              this.requests = [];
              this.logout();
              return throwError(() => res.error);
            }
          }),
          catchError((err) => {
            this.requests = [];
            this.logout();
            this.toastService.toastMessage(
              TOAST_MESSAGES.ERROR,
              MESSAGES.LOGOUT,
            );
            return throwError(() => err);
          }),
        );
    }
    return throwError(() => "err");
  }

  addTokenHeader(request: HttpRequest<any>, token: any) {
    if (request.url.includes("photoUpload")) {
      return request.clone({
        url: `${environment.API_BASE_URL}${request.url}`,
        setHeaders: {
          authorization: token || "",
        },
      });
    }
    return request.clone({
      url: `${environment.API_BASE_URL}${request.url}`,
      setHeaders: {
        authorization: token || "",
        "Content-Type": "application/json",
      },
    });
  }

  logout(): void {
    this.localStorage.clearAll();
    this.dialogRef.closeAll();
    this.route.navigateByUrl("/");
    this.isRefreshing = false;
  }

  private showLoader(): void {
    this.eventService.broadcastEvent(EVENT.SHOW_HIDE_LOADER, true);
  }
  private hideLoader(): void {
    setTimeout(() => {
      this.eventService.broadcastEvent(EVENT.SHOW_HIDE_LOADER, false);
    }, 2000);
  }

  private beforeRequest(req: any): void {
    let requestUrl = req?.urlWithParams;

    this.requestCount++;
    if (req?.method === "GET" && requestUrl.includes("action=global_search")) {
      return;
    }
    if (
      req?.method === "GET" &&
      requestUrl.includes("market-info") &&
      !requestUrl.includes("page=1")
    ) {
      return;
    } else {
      this.showLoader();
    }
  }

  private afterRequest(): void {
    this.requestCount--;
    if (this.requestCount <= 0) {
      this.hideLoader();
      this.requestCount = 0;
    }
  }
}
